import { observer } from 'mobx-react-lite'
import { useState, useMemo, useEffect } from 'react'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { ModalWindow } from 'src/component/common/analog/template/ModalWindow'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { OrganisationLevel } from 'src/feature/b2b/model/OrganisationLevel'
import { AppTextField } from 'src/feature/form/component/analog/AppTextField'
import { AppForm } from 'src/component/common/analog/molecule/basic/AppForm'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { useB2bStore } from 'src/feature/b2b/store/b2b.store'
import { FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Typography } from '@mui/material'
import { AppDivider } from 'src/component/common/analog/molecule/AppDivider'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import { ColorTokens } from 'src/feature/theme/model/Palette'

export type OrgLevelModalProps = {
  show: boolean
  onClose: (reload: boolean) => void
  parentId: string | null
  editLevel: OrganisationLevel | null
  lockedCreateType?: 'team' | 'role'
}

const getDefaultLevelType = (
  editLevel: OrganisationLevel | null,
  parentId: string | null,
  lockedCreateType?: 'team' | 'role',
) => editLevel?.levelType || lockedCreateType || (parentId ? 'role' : 'team')

const generateUniqueKey = (name: string) => {
  const baseKey = name.toLowerCase()
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 40)
  const randomSuffix = Math.random().toString(36)
    .substring(2, 8)

  return `${baseKey}-${randomSuffix}`
}

const getDescendantLevels = (
  organisationLevels: OrganisationLevel[],
  rootLevelId: string,
): OrganisationLevel[] => {
  const descendants: OrganisationLevel[] = []
  const parentIds = [rootLevelId]

  while (parentIds.length > 0) {
    const currentParentId = parentIds.shift()

    if (!currentParentId) {
      continue
    }

    organisationLevels.forEach((level) => {
      if (level.parentLevel === currentParentId) {
        descendants.push(level)
        parentIds.push(level.id)
      }
    })
  }

  return descendants
}

export const OrgLevelModal = observer((props: OrgLevelModalProps) => {
  const { translation } = useLocalizationStore()
  const texts = translation.pages.controlpanelOrganisation.addOrEditDialog
  const buttonTexts = translation.buttons
  const b2bStore = useB2bStore()
  const organisationLevels = b2bStore.organisationLevels || []
  const isEditing = Boolean(props.editLevel)
  const isCreatingChild = Boolean(props.parentId)

  const defaultName = props.editLevel?.name || ''
  const [name, setName] = useState(defaultName)
  const [key, setKey] = useState(props.editLevel?.key || '')
  const [isDeleteConfirmationVisible, setIsDeleteConfirmationVisible] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const lockedCreateType = isEditing ? undefined : props.lockedCreateType

  const defaultType = getDefaultLevelType(props.editLevel, props.parentId, lockedCreateType)
  const [type, setType] = useState(defaultType)

  // Reset state when modal reopens with different props
  useEffect(() => {
    const newDefaultType = getDefaultLevelType(props.editLevel, props.parentId, lockedCreateType)
    setType(newDefaultType)
    setName(props.editLevel?.name || '')
    setKey(props.editLevel?.key || '')
    setIsDeleteConfirmationVisible(false)
  }, [lockedCreateType, props.editLevel, props.parentId])

  const levelType = type

  const showTypeSelection = isCreatingChild || isEditing
  const isTeamOptionDisabled = lockedCreateType === 'role'
  const isRoleOptionDisabled = lockedCreateType === 'team'

  const modalTitle = useMemo(() => {
    if (isDeleteConfirmationVisible) {
      return texts.deleteConfirmTitle()
    }

    if (isEditing) {
      return levelType === 'team' ? texts.editTeamTitle() : texts.editRoleTitle()
    }

    if (isCreatingChild) {
      return type === 'team' ? texts.addSubTeamTitle() : texts.addRoleTitle()
    }

    return texts.addTeamTitle()
  }, [isDeleteConfirmationVisible, isCreatingChild, isEditing, levelType, texts, type])

  const descendantLevels = useMemo(() => {
    if (!props.editLevel) {
      return [] as OrganisationLevel[]
    }

    return getDescendantLevels(organisationLevels, props.editLevel.id)
  }, [organisationLevels, props.editLevel])

  const modalSubtitle = useMemo(() => {
    if (isDeleteConfirmationVisible) {
      return descendantLevels.length > 0
        ? `${descendantLevels.length} linked level(s) will also be removed.`
        : 'This action will permanently remove this level.'
    }

    if (isEditing) {
      return levelType === 'team'
        ? texts.namePlaceholderTeam()
        : texts.namePlaceholderRole()
    }

    if (isCreatingChild) {
      return type === 'team'
        ? texts.subTeamLabel()
        : texts.roleLabel()
    }

    return texts.namePlaceholderTeam()
  }, [descendantLevels.length, isCreatingChild, isDeleteConfirmationVisible, isEditing, levelType, texts, type])

  const headerIcon = useMemo(() => {
    if (isDeleteConfirmationVisible) {
      return <DeleteOutlineOutlinedIcon sx={styles.deleteTitleIcon} />
    }

    if (levelType === 'role') {
      return <BadgeOutlinedIcon sx={styles.roleTitleIcon} />
    }

    return <AccountTreeOutlinedIcon sx={styles.titleIcon} />
  }, [isDeleteConfirmationVisible, levelType])

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newName = event.target.value
    setName(newName)
    
    // Auto-generate key for new items only
    if (!isEditing && newName.trim()) {
      setKey(generateUniqueKey(newName))
    }
  }

  const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isEditing) {
      return
    }

    if (lockedCreateType) {
      setType(lockedCreateType)

      return
    }

    setType(event.target.value as 'team' | 'role')
  }

  const saveSettings = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    await b2bStore.saveOrganisationLevel({
      id: props.editLevel?.id,
      name,
      key,
      parentLevel: props.parentId || undefined,
      levelType,
    })

    props.onClose(true)
  }

  const removeLevel = async () => {
    if (!props.editLevel) {
      return
    }

    setIsDeleting(true)

    try {
      await b2bStore.removeOrganisationLevel(props.editLevel.id)
      props.onClose(true)
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <ModalWindow
      modalProps={{
        open: props.show,
        onClose: () => props.onClose(false),
        PaperProps: {
          sx: {
            maxWidth: { xs: '90%', md: '60%', lg: '30%' },
            borderRadius: '10px',
          },
        },
      }}
      titleProps={{
        textValue: modalTitle,
      }}
      headerIcon={headerIcon}
      subtitleProps={modalSubtitle ? {
        textValue: modalSubtitle,
        variant: 'body2',
        component: 'p',
        sx: styles.subtitleText,
      } : undefined}
    >
      <AppBox sx={styles.container}>
        {isDeleteConfirmationVisible ? (
          <AppBox sx={styles.confirmationContainer}>
            <Typography sx={styles.confirmationText}>
              {levelType === 'team' ? texts.deleteConfirmTeamDescription() : texts.deleteConfirmRoleDescription()}
            </Typography>
            {descendantLevels.length > 0 && (
              <>
                <Typography sx={styles.confirmationText}>
                  {texts.deleteConfirmCascadeDescription()}
                </Typography>
                <AppBox sx={styles.deleteList}>
                  {descendantLevels.map(level => (
                    <Typography key={level.id} sx={styles.deleteListItem}>
                      {`• ${level.name}`}
                    </Typography>
                  ))}
                </AppBox>
              </>
            )}

            <AppBox sx={styles.deleteActions}>
              <AppButton
                fontSize={12}
                variant="light"
                fullWidth={false}
                textValue={buttonTexts.cancel()}
                onClick={() => setIsDeleteConfirmationVisible(false)}
                disabled={isDeleting}
              />
              <AppButton
                fontSize={12}
                variant="danger"
                fullWidth={false}
                textValue={buttonTexts.confirm()}
                onClick={removeLevel}
                disabled={isDeleting}
              />
            </AppBox>
          </AppBox>
        ) : (
          <AppForm sx={styles.form} onSubmit={saveSettings}>
            {showTypeSelection && (
              <FormControl sx={styles.typeSelection} disabled={isEditing}>
                <FormLabel sx={styles.label}>{texts.selectTypeLabel()}</FormLabel>
                <RadioGroup value={type} onChange={handleTypeChange}>
                  <FormControlLabel
                    value="team"
                    control={<Radio />}
                    label={texts.subTeamLabel()}
                    disabled={isEditing || isTeamOptionDisabled}
                    sx={styles.radioLabel}
                  />
                  <FormControlLabel
                    value="role"
                    control={<Radio />}
                    label={texts.roleLabel()}
                    disabled={isEditing || isRoleOptionDisabled}
                    sx={styles.radioLabel}
                  />
                </RadioGroup>
              </FormControl>
            )}
            
            <AppTextField
              sx={{ margin: 0 }}
              label={levelType === 'team' ? texts.teamNameField() : texts.roleNameField()}
              placeholder={levelType === 'team' ? texts.namePlaceholderTeam() : texts.namePlaceholderRole()}
              value={name}
              onChange={handleNameChange}
              fullWidth
              required
            />
            
            <AppTextField
              sx={{ margin: 0 }}
              label={texts.technicalKeyField()}
              placeholder={texts.technicalKeyPlaceholder()}
              value={key}
              fullWidth
              disabled
            />
         
            <AppDivider />

            <AppBox sx={styles.saveButton}>
              {isEditing && (
                <AppButton
                  fontSize={12}
                  variant="danger"
                  fullWidth={false}
                  textValue={texts.deleteButton}
                  onClick={() => setIsDeleteConfirmationVisible(true)}
                />
              )}
              <AppButton
                fontSize={12}
                variant="contained"
                fullWidth={false}
                textValue={texts.saveButton}
                type='submit'
              />
            </AppBox>
          </AppForm>
        )}
      </AppBox>
    </ModalWindow>
  )
})

const styles = {
  container: {
    flexDirection: 'column',
    gap: 10,
  },
  form: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 10,
  },
  confirmationContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  confirmationText: {
    color: 'text.primary',
    fontSize: 14,
    lineHeight: 1.5,
  },
  deleteList: {
    flexDirection: 'column',
    gap: 4,
    paddingLeft: 8,
  },
  deleteListItem: {
    color: 'text.primary',
    fontSize: 13,
    lineHeight: 1.5,
  },
  deleteActions: {
    alignSelf: 'flex-end',
    gap: 10,
  },
  titleIcon: {
    fontSize: 18,
    color: ColorTokens.primary400,
  },
  roleTitleIcon: {
    fontSize: 18,
    color: ColorTokens.secondary400,
  },
  deleteTitleIcon: {
    fontSize: 18,
    color: ColorTokens.red100,
  },
  subtitleText: {
    color: 'text.secondary',
    lineHeight: 1.4,
  },
  typeSelection: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 2,
    width: '100%',
  },
  radioLabel: {
    margin: 0,
    '& .MuiFormControlLabel-label': {
      fontWeight: 400,
      fontSize: 12,
      lineHeight: 1.28,
      fontStyle: 'normal',
    },
  },
  saveButton: {
    alignSelf: 'flex-end',
    gap: 10,
  },
  label: {
    color: 'text.primary',
    fontWeight: 400,
    fontSize: 12,
    lineHeight: 1.28,
    fontStyle: 'normal',
  },
}